import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { calculateStreak, getEarnedBadges, getNextBadge } from '@/lib/verificationLogic';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STORAGE_KEYS = {
  HISTORY: 'verilink_history',
  STATS: 'verilink_stats',
};

const initialStats = {
  totalVerifications: 0,
  linksVerified: 0,
  articlesVerified: 0,
  emailsVerified: 0,
  comparisonsRun: 0,
  phishingLabSessions: 0,
  threatResearchCount: 0,
  misinformationFound: 0,
  dangerousFound: 0,
  suspiciousFound: 0,
  safeFound: 0,
  streak: 0,
  lastVerificationDate: null,
};

// Helper to safely parse JSON from localStorage
const getStoredData = (key, fallback) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch (e) {
    console.error(`Failed to parse ${key}`, e);
    return fallback;
  }
};

// Helper to check dark mode preference
const getInitialDarkMode = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return false;
};

export const AppProvider = ({ children }) => {
  const { isAuthenticated, token, user } = useAuth();
  
  const [history, setHistory] = useState(() => getStoredData(STORAGE_KEYS.HISTORY, []));
  const [stats, setStats] = useState(() => getStoredData(STORAGE_KEYS.STATS, initialStats));
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const [activeView, setActiveView] = useState('link');
  const [newBadge, setNewBadge] = useState(null);
  const [syncedHistory, setSyncedHistory] = useState([]);

  // Apply dark mode class on mount
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Save to localStorage when history/stats change (for guests)
  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(history));
    }
  }, [history, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(stats));
    }
  }, [stats, isAuthenticated]);

  // Fetch history from server when logged in
  useEffect(() => {
    const fetchHistory = async () => {
      if (isAuthenticated) {
        try {
          const response = await fetch(`${API_URL}/api/verification/history?limit=100`, {
            credentials: 'include',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          
          if (response.ok) {
            const data = await response.json();
            // Transform server history to match local format
            const serverHistory = data.verifications.map(v => ({
              id: v._id,
              type: v.type,
              input: v.input,
              score: v.score,
              verdict: v.verdict,
              safetyScore: v.type === 'link' ? v.score : undefined,
              credibilityScore: (v.type === 'article' || v.type === 'article-comparison') ? v.score : undefined,
              riskScore: v.type === 'email' ? v.score : undefined,
              flagCount: v.flagCount,
              criticalFlags: v.criticalFlags,
              result: v.result || {},
              timestamp: v.timestamp,
              synced: true
            }));
            setSyncedHistory(serverHistory);
          }
        } catch (err) {
          console.error('Failed to fetch history:', err);
        }
      }
    };

    fetchHistory();
  }, [isAuthenticated, token]);

  // Update stats from user data when logged in
  useEffect(() => {
    if (isAuthenticated && user?.stats) {
      setStats({
        totalVerifications: user.stats.totalVerifications || 0,
        linksVerified: user.stats.linksVerified || 0,
        articlesVerified: user.stats.articlesVerified || 0,
        emailsVerified: user.stats.emailsVerified || 0,
        comparisonsRun: user.stats.comparisonsRun || 0,
        phishingLabSessions: user.stats.phishingLabSessions || 0,
        threatResearchCount: user.stats.threatResearchCount || 0,
        misinformationFound: user.stats.misinformationFound || 0,
        dangerousFound: user.stats.threatsDetected || 0,
        suspiciousFound: 0,
        safeFound: (user.stats.totalVerifications || 0) - (user.stats.threatsDetected || 0),
        streak: user.stats.streak || 0,
        lastVerificationDate: user.stats.lastVerificationDate,
      });
    }
  }, [isAuthenticated, user]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newValue = !prev;
      if (newValue) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return newValue;
    });
  };

  // Save verification to server
  const saveToServer = useCallback(async (verification) => {
    if (!isAuthenticated) return null;

    try {
      const response = await fetch(`${API_URL}/api/verification`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          type: verification.type,
          input: verification.input,
          score: verification.safetyScore || verification.credibilityScore || verification.riskScore || 0,
          verdict: verification.verdict || verification.category || 'Unknown',
          result: verification.result || {},
          flagCount: verification.flagCount || 0,
          criticalFlags: verification.hardRedFlags || verification.criticalFlags || 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.verification;
      }
    } catch (err) {
      console.error('Failed to save verification to server:', err);
    }
    return null;
  }, [isAuthenticated, token]);

  // Add verification to history
  const addVerification = useCallback(async (verification) => {
    const newEntry = {
      ...verification,
      id: Date.now(),
      timestamp: new Date().toISOString(),
    };

    // Save locally first
    setHistory(prev => [newEntry, ...prev].slice(0, 100));

    // Update local stats
    const oldBadges = getEarnedBadges(stats);
    
    setStats(prev => {
      const verdict = verification.verdict || verification.category;
      const isLabMode = verification.type === 'phishing-lab' &&
        ['attacker', 'defender', 'linguistics'].includes((verification.result || {}).mode);

      const newStats = {
        ...prev,
        totalVerifications: prev.totalVerifications + 1,
        linksVerified: verification.type === 'link' ? prev.linksVerified + 1 : prev.linksVerified,
        articlesVerified: verification.type === 'article' ? prev.articlesVerified + 1 : prev.articlesVerified,
        emailsVerified: verification.type === 'email' ? prev.emailsVerified + 1 : prev.emailsVerified,
        comparisonsRun: verification.type === 'article-comparison' ? (prev.comparisonsRun || 0) + 1 : (prev.comparisonsRun || 0),
        phishingLabSessions: verification.type === 'phishing-lab' ? (prev.phishingLabSessions || 0) + 1 : (prev.phishingLabSessions || 0),
        threatResearchCount: isLabMode ? (prev.threatResearchCount || 0) + 1 : (prev.threatResearchCount || 0),
        misinformationFound: (verification.type === 'article' && verdict === 'Low Credibility')
          ? (prev.misinformationFound || 0) + 1
          : (prev.misinformationFound || 0),
        lastVerificationDate: new Date().toISOString(),
      };

      // Update verdict counts
      if (verdict === 'Dangerous' || verdict === 'High Phishing Risk' || verdict === 'Low Credibility') {
        newStats.dangerousFound = prev.dangerousFound + 1;
      } else if (verdict === 'Caution' || verdict === 'Suspicious' || verdict === 'Mixed / Uncertain') {
        newStats.suspiciousFound = prev.suspiciousFound + 1;
      } else {
        newStats.safeFound = prev.safeFound + 1;
      }

      // Calculate streak
      newStats.streak = calculateStreak([newEntry, ...history]);

      return newStats;
    });

    // If logged in, also save to server
    if (isAuthenticated) {
      const serverEntry = await saveToServer(verification);
      if (serverEntry) {
        // Add to synced history
        setSyncedHistory(prev => [{
          ...newEntry,
          id: serverEntry._id,
          synced: true
        }, ...prev].slice(0, 100));
      }
    }

    // Check for new badges
    setTimeout(() => {
      const verdict = verification.verdict || verification.category;
      const isLabMode = verification.type === 'phishing-lab' &&
        ['attacker', 'defender', 'linguistics'].includes((verification.result || {}).mode);
      const updatedStats = {
        ...stats,
        totalVerifications: stats.totalVerifications + 1,
        streak: calculateStreak([newEntry, ...history]),
        dangerousFound: (verdict === 'Dangerous' || verdict === 'High Phishing Risk' || verdict === 'Low Credibility')
          ? stats.dangerousFound + 1
          : stats.dangerousFound,
        comparisonsRun: verification.type === 'article-comparison'
          ? (stats.comparisonsRun || 0) + 1
          : (stats.comparisonsRun || 0),
        phishingLabSessions: verification.type === 'phishing-lab'
          ? (stats.phishingLabSessions || 0) + 1
          : (stats.phishingLabSessions || 0),
        threatResearchCount: isLabMode
          ? (stats.threatResearchCount || 0) + 1
          : (stats.threatResearchCount || 0),
        misinformationFound: (verification.type === 'article' && verdict === 'Low Credibility')
          ? (stats.misinformationFound || 0) + 1
          : (stats.misinformationFound || 0),
      };
      
      const newBadges = getEarnedBadges(updatedStats);
      const earnedNew = newBadges.find(b => !oldBadges.some(ob => ob.id === b.id));
      
      if (earnedNew) {
        setNewBadge(earnedNew);
        setTimeout(() => setNewBadge(null), 5000);
      }
    }, 100);

    return newEntry;
  }, [history, stats, isAuthenticated, saveToServer]);

  // Clear history
  const clearHistory = () => {
    setHistory([]);
    if (!isAuthenticated) {
      localStorage.removeItem(STORAGE_KEYS.HISTORY);
    }
  };

  // Reset stats
  const resetStats = () => {
    setStats(initialStats);
    setHistory([]);
  };

  // Combined history (server + local for guests)
  const displayHistory = isAuthenticated ? syncedHistory : history;

  const earnedBadges = getEarnedBadges(stats);
  const nextBadge = getNextBadge(stats);

  return (
    <AppContext.Provider
      value={{
        history: displayHistory,
        stats,
        darkMode,
        activeView,
        newBadge,
        earnedBadges,
        nextBadge,
        setActiveView,
        toggleDarkMode,
        addVerification,
        clearHistory,
        resetStats,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
